import { parseColorToRgb } from "./erRendererStyleUtils";

const DEFAULT_QUIZ_BORDER_RGBA = "rgba(226,232,240,1)";

const DEFAULT_QUIZ_TRUE_COLOR = "#22c55e";
const DEFAULT_QUIZ_FALSE_COLOR = "#ef4444";

function hexToRgba(hex, alpha = 1) {
	if (!hex || typeof hex !== "string") return undefined;
	const h = hex.replace(/^#/, "");
	const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
	const n = parseInt(x, 16);
	if (Number.isNaN(n)) return undefined;
	const r = (n >> 16) & 255;
	const g = (n >> 8) & 255;
	const b = n & 255;
	return `rgba(${r},${g},${b},${alpha})`;
}

/** Colores de verdadero/falso para quizzes (p. ej. pregunta tipo trueFalse). Fondo con opacidad 1. */
function colorLuminance(rgb) {
	if (!rgb) return null;
	return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
}

/** Asegura contraste legible entre texto y fondo tras feedback de corrección. */
function resolveReadableQuizOptionColor(bg, optionColor, { onLight = "#0f172a", onDark = "#f8fafc" } = {}) {
	const bgRgb = parseColorToRgb(bg);
	if (!bgRgb) return optionColor;
	const bgLum = colorLuminance(bgRgb);
	const textRgb = parseColorToRgb(optionColor);
	const textLum = colorLuminance(textRgb);
	if (bgLum > 155 && (textLum == null || textLum > 170)) return onLight;
	if (bgLum < 95 && (textLum == null || textLum < 90)) return onDark;
	return optionColor;
}

export function getQuizTrueFalseColors(element) {
	const content = element?.content || {};
	const rawTrue = (content.quizTrueColor && String(content.quizTrueColor).trim()) || "";
	const rawFalse = (content.quizFalseColor && String(content.quizFalseColor).trim()) || "";
	const hexLike = (s) => /^#?[a-fA-F0-9]{6}$/.test(s) || (s.length === 6 && /^[a-fA-F0-9]+$/.test(s));
	const trueColor = hexLike(rawTrue) ? (rawTrue.startsWith("#") ? rawTrue : `#${rawTrue}`) : DEFAULT_QUIZ_TRUE_COLOR;
	const falseColor = hexLike(rawFalse) ? (rawFalse.startsWith("#") ? rawFalse : `#${rawFalse}`) : DEFAULT_QUIZ_FALSE_COLOR;
	return {
		trueColor,
		falseColor,
		trueBg: hexToRgba(trueColor, 1) || "#f0fdf4",
		falseBg: hexToRgba(falseColor, 1) || "#fef2f2",
	};
}

/**
 * Diseños predeterminados bien diferenciados: cada uno es un patrón de UI distinto
 * (no solo cambio de color/borde): tarjetas con letra, radio, segmentado, burbuja, etc.
 */
export const QUIZ_OPTION_DESIGNS = {
	classic: {
		layout: "default",
		padding: "10px 14px",
		borderRadius: 8,
		borderWidth: 1,
		defaultBg: "#fff",
		defaultBorder: "#e2e8f0",
		correctBg: "#f0fdf4",
		correctBorder: "#22c55e",
		incorrectBg: "#fef2f2",
		incorrectBorder: "#ef4444",
		boxShadow: "none",
		iconPosition: "both",
	},
	letter: {
		layout: "letter",
		padding: "16px 20px",
		borderRadius: 16,
		borderWidth: 1,
		defaultBg: "#fff",
		defaultBorder: "#e2e8f0",
		correctBg: "#ecfdf5",
		correctBorder: "#10b981",
		incorrectBg: "#fef2f2",
		incorrectBorder: "#ef4444",
		boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
		iconPosition: "right",
		letterCircleDefaultBg: "#f1f5f9",
		letterCircleDefaultColor: "#475569",
		letterCircleCorrectBg: "#10b981",
		letterCircleIncorrectBg: "#ef4444",
	},
	segmented: {
		layout: "segmented",
		padding: "14px 20px",
		borderRadius: 12,
		borderWidth: 1,
		defaultBg: "#f8fafc",
		defaultBorder: "#e2e8f0",
		correctBg: "#dcfce7",
		correctBorder: "#22c55e",
		incorrectBg: "#fee2e2",
		incorrectBorder: "#ef4444",
		boxShadow: "none",
		iconPosition: "right",
		segmentGap: 0,
	},
};

export function getQuizOptionStyles(element, state, context = {}) {
	const c = element?.content?.quizOptionsCustom || {};
	const designId = element?.content?.quizOptionsDesign || "classic";
	const design = QUIZ_OPTION_DESIGNS[designId] || QUIZ_OPTION_DESIGNS.classic;
	const questionType = context.questionType;
	const isTrueFalse = questionType === "trueFalse";
	const hasDefinedTfColors = !!(element?.content?.quizTrueColor || element?.content?.quizFalseColor);
	const applyTfColors = isTrueFalse && hasDefinedTfColors;
	const tfColors = getQuizTrueFalseColors(element);
	const styleColorRaw = element?.content?.quizOptionsStyle?.color;
	const styleColor = styleColorRaw && String(styleColorRaw).trim() ? String(styleColorRaw).trim() : "";
	const designMerged = {
		...design,
		correctBg: applyTfColors && element?.content?.quizTrueColor && tfColors.trueBg ? tfColors.trueBg : design.correctBg,
		correctBorder: applyTfColors && element?.content?.quizTrueColor && tfColors.trueColor ? tfColors.trueColor : design.correctBorder,
		incorrectBg: applyTfColors && element?.content?.quizFalseColor && tfColors.falseBg ? tfColors.falseBg : design.incorrectBg,
		incorrectBorder: applyTfColors && element?.content?.quizFalseColor && tfColors.falseColor ? tfColors.falseColor : design.incorrectBorder,
		letterCircleCorrectBg: applyTfColors && element?.content?.quizTrueColor && tfColors.trueColor ? tfColors.trueColor : design.letterCircleCorrectBg,
		letterCircleIncorrectBg: applyTfColors && element?.content?.quizFalseColor && tfColors.falseColor ? tfColors.falseColor : design.letterCircleIncorrectBg,
		defaultOptionColor: styleColor || design.defaultOptionColor,
	};
	if (c.optionBg && String(c.optionBg).trim()) {
		const rawBg = String(c.optionBg).trim();
		if (rawBg.toLowerCase() !== "transparent") {
			const blockBg = String(getQuizBlockBackground(element) || "").trim().toLowerCase();
			designMerged.defaultBg = rawBg.toLowerCase() === blockBg ? "#fff" : rawBg;
		}
	}
	if (c.optionBorderColor && String(c.optionBorderColor).trim()) designMerged.defaultBorder = String(c.optionBorderColor).trim();
	if (c.optionBorderWidth != null && c.optionBorderWidth !== "" && Number.isFinite(Number(c.optionBorderWidth))) {
		designMerged.borderWidth = Math.max(0, Math.min(10, Number(c.optionBorderWidth)));
	}
	const hasCustomOptionBg = !!(c.optionBg && String(c.optionBg).trim() && String(c.optionBg).trim().toLowerCase() !== "transparent" && String(c.optionBg).trim().toLowerCase() !== String(getQuizBlockBackground(element) || "").trim().toLowerCase());
	if (c.markerFill && String(c.markerFill).trim()) designMerged.letterCircleDefaultBg = String(c.markerFill).trim();
	if (c.markerBorder && String(c.markerBorder).trim()) designMerged.letterCircleDefaultColor = String(c.markerBorder).trim();
	const markerEnabled = !!(String(c.markerBorder || "").trim() || String(c.markerFill || "").trim() || c.markerSize != null || (c.markerShape && String(c.markerShape).trim()));
	const markerSize = Math.max(14, Math.min(44, Number(c.markerSize) || 24));
	const markerBorderW = Math.max(1, Math.min(5, Number(c.markerBorderWidth) || 2));
	const markerBorderColorResolved = String(c.markerBorder || "").trim() || "#64748b";
	const markerFillResolved = String(c.markerFill || "").trim() || "transparent";
	const markerShapeTrim = String(c.markerShape ?? "").trim();
	const markerShapeNorm = markerShapeTrim.toLowerCase();
	const shapeExplicit = markerShapeTrim !== "";
	const shapeRound = shapeExplicit && (markerShapeNorm === "round" || markerShapeNorm === "circle" || markerShapeNorm === "redonda");
	const cornerPx = Number.isFinite(Number(c.markerRadius)) ? Math.max(0, Math.min(12, Number(c.markerRadius))) : (shapeRound ? 4 : 2);
	const markerCustom = markerEnabled
		? {
			enabled: true,
			size: markerSize,
			borderWidth: markerBorderW,
			borderColor: markerBorderColorResolved,
			fill: markerFillResolved,
			shapeRound,
			cornerPx: `${cornerPx}px`,
		}
		: { enabled: false };

	const { done, isSelected, correct, sel } = state;
	const { index = 0, totalCount = 1 } = context;
	const surveyMode = !!context.surveyMode;
	const hasCustomTfColors = applyTfColors;

	let bg = designMerged.defaultBg;
	let border = designMerged.defaultBorder;
	let optionColor = designMerged.defaultOptionColor;
	let borderRadius = designMerged.borderRadius;
	let boxShadow = designMerged.boxShadow;
	let borderBottomWidth = designMerged.borderBottomWidth;
	let borderBottomColor = designMerged.correctBorderBottomColor;

	if (surveyMode && isSelected) {
		if (!hasCustomOptionBg) bg = designMerged.defaultBg;
		border = "#818cf8";
		if (!hasCustomOptionBg) optionColor = optionColor || "#312e81";
		boxShadow = "0 2px 14px rgba(99,102,241,0.22)";
		borderBottomWidth = designMerged.borderWidth != null ? designMerged.borderWidth : 2;
		borderBottomColor = border;
	} else if (hasCustomTfColors && (done || isSelected)) {
		if (correct) {
			if (!hasCustomOptionBg) bg = designMerged.correctBg;
			border = designMerged.correctBorder;
			if (!hasCustomOptionBg) optionColor = designMerged.correctOptionColor || optionColor;
			boxShadow = designMerged.correctBoxShadow || boxShadow;
			borderBottomWidth = designMerged.correctBorderBottomWidth;
			borderBottomColor = designMerged.correctBorderBottomColor;
		} else if (isSelected && sel && !sel.correct) {
			if (!hasCustomOptionBg) bg = designMerged.incorrectBg;
			border = designMerged.incorrectBorder;
			if (!hasCustomOptionBg) optionColor = designMerged.incorrectOptionColor || optionColor;
			boxShadow = designMerged.incorrectBoxShadow || boxShadow;
			borderBottomWidth = designMerged.incorrectBorderBottomWidth;
			borderBottomColor = designMerged.incorrectBorderBottomColor;
		}
	} else if (done || isSelected) {
		if (correct) {
			if (!hasCustomOptionBg) bg = designMerged.correctBg;
			border = designMerged.correctBorder;
			if (!hasCustomOptionBg) optionColor = designMerged.correctOptionColor || optionColor;
			boxShadow = designMerged.correctBoxShadow || boxShadow;
			borderBottomWidth = designMerged.correctBorderBottomWidth;
			borderBottomColor = designMerged.correctBorderBottomColor;
		} else if (isSelected && sel && !sel.correct) {
			if (!hasCustomOptionBg) bg = designMerged.incorrectBg;
			border = designMerged.incorrectBorder;
			if (!hasCustomOptionBg) optionColor = designMerged.incorrectOptionColor || optionColor;
			boxShadow = designMerged.incorrectBoxShadow || boxShadow;
			borderBottomWidth = designMerged.incorrectBorderBottomWidth;
			borderBottomColor = designMerged.incorrectBorderBottomColor;
		}
	}

	const hasCustomBorderRadius = c.optionBorderRadius != null && c.optionBorderRadius !== "" && Number.isFinite(Number(c.optionBorderRadius));
	if (hasCustomBorderRadius) {
		borderRadius = Math.max(0, Math.min(50, Number(c.optionBorderRadius)));
	} else if (designMerged.layout === "segmented" && totalCount > 1) {
		if (index === 0) borderRadius = "12px 0 0 12px";
		else if (index === totalCount - 1) borderRadius = "0 12px 12px 0";
		else borderRadius = 0;
	}

	const hoverBgCustom = (c.optionBgHover && String(c.optionBgHover).trim()) || null;
	const hoverTextCustom =
		(element?.content?.quizOptionsStyle?.hoverColor && String(element.content.quizOptionsStyle.hoverColor).trim()) ||
		(c.optionTextHover && String(c.optionTextHover).trim()) ||
		null;

	const inFeedback = !surveyMode && (done || isSelected) && (correct || (isSelected && sel && !sel.correct));
	if (surveyMode && isSelected) {
		if (!hasCustomOptionBg) optionColor = styleColor || optionColor || "#312e81";
	} else if (inFeedback) {
		if (hasCustomOptionBg) {
			optionColor = styleColor || optionColor || "#f8fafc";
		} else {
			optionColor = resolveReadableQuizOptionColor(bg, styleColor || optionColor, {
				onLight: correct ? "#14532d" : "#7f1d1d",
				onDark: "#f8fafc",
			});
		}
	} else if ((done || isSelected) && !hasCustomOptionBg) {
		const rgbBg = parseColorToRgb(bg);
		if (rgbBg != null) {
			const lum = colorLuminance(rgbBg);
			if (lum > 155 && (optionColor === undefined || optionColor === null)) optionColor = "#0f172a";
		}
	}

	return {
		layout: designMerged.layout || "default",
		backgroundColor: bg,
		borderColor: border,
		borderWidth: designMerged.borderWidth,
		borderRadius,
		padding: designMerged.padding,
		boxShadow: boxShadow || undefined,
		iconLeft: designMerged.iconPosition === "left" || designMerged.iconPosition === "both",
		iconRight: designMerged.iconPosition === "right" || designMerged.iconPosition === "both",
		optionColor: optionColor || undefined,
		hoverBackgroundColor: hoverBgCustom || undefined,
		hoverOptionColor: hoverTextCustom || undefined,
		borderBottomWidth: borderBottomWidth != null ? borderBottomWidth : undefined,
		borderBottomColor: borderBottomColor || undefined,
		letterCircleDefaultBg: designMerged.letterCircleDefaultBg,
		letterCircleDefaultColor: designMerged.letterCircleDefaultColor,
		letterCircleCorrectBg: designMerged.letterCircleCorrectBg,
		letterCircleIncorrectBg: designMerged.letterCircleIncorrectBg,
		radioSize: designMerged.radioSize,
		trueColor: tfColors.trueColor,
		falseColor: tfColors.falseColor,
		applyTfColorsByDefault: !!hasCustomTfColors,
		markerCustom,
		markerShapeIsRound: shapeExplicit ? shapeRound : undefined,
		markerCornerCss: `${cornerPx}px`,
	};
}

function normalizeQuizOptionExtraBoxShadow(extraBoxShadow) {
	if (!extraBoxShadow || extraBoxShadow === "none") return undefined;
	return extraBoxShadow;
}

function resolveQuizOptionBorderWidth(borderWidth, fallback = 1) {
	if (borderWidth != null && borderWidth !== "" && Number.isFinite(Number(borderWidth))) {
		return Math.max(0, Math.min(10, Number(borderWidth)));
	}
	return fallback;
}

/** Marco de opción quiz: borde CSS completo + sombra opcional (sin mezclar con "none"). */
export function resolveQuizOptionBorderPresentation(borderWidth, borderColor, extraBoxShadow) {
	const w = resolveQuizOptionBorderWidth(borderWidth);
	const extra = normalizeQuizOptionExtraBoxShadow(extraBoxShadow);
	if (!w) {
		return { border: "none", boxShadow: extra };
	}
	const col = String(borderColor || "").trim() || "#e2e8f0";
	return {
		border: `${w}px solid ${col}`,
		boxShadow: extra,
	};
}

/** @deprecated Usar resolveQuizOptionBorderPresentation */
export function resolveQuizOptionBorderCss(borderWidth, borderColor) {
	const w = resolveQuizOptionBorderWidth(borderWidth);
	if (!w) return "none";
	const col = String(borderColor || "").trim() || "#e2e8f0";
	return `${w}px solid ${col}`;
}

/** Fondo del bloque quiz (RGBA); compatible con elementos antiguos que solo tenían style.backgroundColor. */
export function getQuizBlockBackground(element) {
	const c = element?.content?.quizBackgroundRgba?.trim();
	if (c) return c;
	const s = element?.style?.backgroundColor;
	if (!s || s === "transparent") return "rgba(255,255,255,1)";
	if (String(s).startsWith("rgba(")) return s;
	if (String(s).startsWith("rgb(")) return String(s).replace(/^rgb\(/, "rgba(").replace(/\)$/, ", 1)");
	if (String(s).startsWith("#")) {
		const h = String(s).slice(1);
		const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
		const n = parseInt(x, 16);
		if (Number.isNaN(n)) return "rgba(255,255,255,1)";
		return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},1)`;
	}
	return "rgba(255,255,255,1)";
}

/** Borde del bloque quiz: grosor (px) en content.quizBorderWidth (0 = sin borde); color en content.quizBorderRgba. */
export function getQuizBlockBorderCss(element) {
	const content = element?.content || {};
	const wRaw = content.quizBorderWidth;
	const width = wRaw === undefined || wRaw === null ? 1 : Math.max(0, Math.min(10, Number(wRaw)));
	if (!Number.isFinite(width) || width <= 0) return "none";
	const c = String(content.quizBorderRgba || "").trim();
	let color = DEFAULT_QUIZ_BORDER_RGBA;
	if (c) {
		if (/^rgba\(/i.test(c)) color = c;
		else if (/^rgb\(/i.test(c)) color = c.replace(/^rgb\(/i, "rgba(").replace(/\)$/, ", 1)");
		else if (c.startsWith("#")) {
			const h = c.slice(1);
			const x = h.length === 3 ? h.split("").map((ch) => ch + ch).join("") : h;
			const n = parseInt(x, 16);
			if (!Number.isNaN(n)) color = `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},1)`;
		}
	}
	return `${width}px solid ${color}`;
}

const DEFAULT_QUIZ_BLOCK_BOX_SHADOW = "0 2px 8px rgba(0,0,0,0.08)";

export function getQuizBlockShadowBlur(element) {
	const w = element?.content?.quizBlockShadowBlur;
	if (w === undefined || w === null || w === "") return 8;
	const n = Number(w);
	return Number.isFinite(n) ? Math.max(0, Math.min(24, n)) : 8;
}

export function getQuizBlockBoxShadow(element) {
	const content = element?.content || {};
	const blurRaw = content.quizBlockShadowBlur;
	const opRaw = content.quizBlockShadowOpacity;
	const blurExplicit = blurRaw !== undefined && blurRaw !== null && blurRaw !== "";
	const opacityExplicit = opRaw != null && opRaw !== "" && Number.isFinite(Number(opRaw));
	if (!blurExplicit && !opacityExplicit) return DEFAULT_QUIZ_BLOCK_BOX_SHADOW;
	const blur = blurExplicit ? Number(blurRaw) : 8;
	if (!Number.isFinite(blur) || blur <= 0) return "none";
	const b = Math.max(1, Math.min(24, blur));
	const alpha = opacityExplicit ? Math.max(0, Math.min(100, Number(opRaw))) / 100 : 0.08;
	return `0 2px ${b}px rgba(0,0,0,${alpha})`;
}

function mergeTextAnswerBoxShadow(base, extra) {
	if (base && extra && base !== "none" && extra !== "none") return `${base}, ${extra}`;
	if (base && base !== "none") return base;
	if (extra && extra !== "none") return extra;
	return undefined;
}

export function buildTextAnswerFieldPresentation({
	element,
	optionTextStyle,
	optionBoxFx = {},
	state = {},
	context = {},
}) {
	const { done = false, isSelected = false, correct = false, sel = null } = state;
	const optStyles = getQuizOptionStyles(element, { done, isSelected, correct, sel }, context);
	const optionBorderStyle = resolveQuizOptionBorderPresentation(
		optStyles.borderWidth,
		optStyles.borderColor,
		optStyles.boxShadow,
	);
	// Solo boxShadow estático: filter/glitch/CSS vars en inputs nativos generan marcos magenta y capas fantasma.
	const optionBoxFxShadow = optionBoxFx.extraStyle?.boxShadow;
	const isBigButtons = optStyles?.layout === "bigButtons";
	const containerStyle = {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		width: "100%",
		alignSelf: "stretch",
		display: "flex",
		alignItems: isBigButtons ? "center" : "stretch",
		justifyContent: isBigButtons ? "center" : undefined,
		boxSizing: "border-box",
		overflow: "hidden",
		padding: optStyles?.padding,
		borderRadius: optStyles?.borderRadius,
		background: optStyles?.backgroundColor,
		...optionBorderStyle,
		boxShadow: mergeTextAnswerBoxShadow(optionBorderStyle.boxShadow, optionBoxFxShadow),
	};
	const inputStyle = {
		...optionTextStyle,
		color: optStyles?.optionColor || optionTextStyle?.color,
		flex: 1,
		minWidth: 0,
		minHeight: 0,
		width: "100%",
		height: "100%",
		alignSelf: "stretch",
		border: "none",
		background: "transparent",
		outline: "none",
		boxShadow: "none",
		borderRadius: "inherit",
		WebkitAppearance: "none",
		appearance: "none",
		WebkitTapHighlightColor: "transparent",
		boxSizing: "border-box",
	};
	const submitOptStyles = getQuizOptionStyles(
		element,
		{ done: false, isSelected: false, correct: false, sel: null },
		context
	);
	const submitBorderStyle = resolveQuizOptionBorderPresentation(
		submitOptStyles.borderWidth,
		submitOptStyles.borderColor,
		submitOptStyles.boxShadow
	);
	const submitButtonStyle = {
		background: submitOptStyles?.backgroundColor,
		color: submitOptStyles?.optionColor || optionTextStyle?.color,
		borderRadius: submitOptStyles?.borderRadius,
		padding: submitOptStyles?.padding ?? "0 16px",
		boxSizing: "border-box",
		WebkitAppearance: "none",
		appearance: "none",
		...submitBorderStyle,
		boxShadow: mergeTextAnswerBoxShadow(submitBorderStyle.boxShadow, optionBoxFxShadow),
	};
	return {
		containerStyle,
		inputStyle,
		className: undefined,
		submitButtonStyle,
		submitButtonClassName: undefined,
	};
}
